import { ArrowDown, ArrowUp, Image as ImageIcon, Pencil, Trash2 } from 'lucide-react';
import { resolveStoreCategoryPublicDescription, useTranslation } from '@dukanti/shared';
import type { StoreCategoryWithMedia } from '@dukanti/shared';
import { Button } from '@/components/ui/button';

export interface CategoryListProps {
  readonly categories: readonly StoreCategoryWithMedia[];
  readonly isReordering?: boolean;
  readonly onEdit: (category: StoreCategoryWithMedia) => void;
  readonly onDelete: (category: StoreCategoryWithMedia) => void;
  readonly onMove: (category: StoreCategoryWithMedia, direction: 'up' | 'down') => void;
}

export function CategoryList({
  categories,
  isReordering = false,
  onEdit,
  onDelete,
  onMove,
}: CategoryListProps) {
  const { t, lang } = useTranslation();

  return (
    <div
      data-testid="category-list"
      className="border-border bg-card shadow-admin-card overflow-hidden rounded-lg border"
    >
      <ul className="divide-border divide-y">
        {categories.map((category, index) => {
          const isFirst = index === 0;
          const isLast = index === categories.length - 1;
          const description = resolveStoreCategoryPublicDescription(
            category.public_description,
            lang,
          );
          return (
            <li
              key={category.id}
              data-testid={`category-row-${category.id}`}
              className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center"
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div className="bg-admin-surface-muted border-border flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border">
                  {category.media?.url ? (
                    <img
                      src={category.media.url}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <ImageIcon
                      className="text-muted-foreground size-6"
                      strokeWidth={1.5}
                      aria-hidden
                    />
                  )}
                </div>
                <div className="min-w-0">
                  <p
                    className="text-foreground truncate text-start font-medium"
                    data-testid={`category-row-${category.id}-name`}
                  >
                    {category.name}
                  </p>
                  <p className="text-muted-foreground text-start text-xs">
                    {t('admin.categories.list.order', { order: index + 1 })}
                  </p>
                  {description ? (
                    <p
                      className="text-muted-foreground mt-1 line-clamp-2 max-w-2xl whitespace-normal break-words text-start text-xs leading-5"
                      data-testid={`category-row-${category.id}-public-description`}
                    >
                      {description}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-1.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={isFirst || isReordering}
                  aria-label={t('admin.categories.actions.moveUp', { name: category.name })}
                  onClick={() => onMove(category, 'up')}
                >
                  <ArrowUp className="size-4" aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={isLast || isReordering}
                  aria-label={t('admin.categories.actions.moveDown', { name: category.name })}
                  onClick={() => onMove(category, 'down')}
                >
                  <ArrowDown className="size-4" aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t('admin.categories.actions.edit', { name: category.name })}
                  onClick={() => onEdit(category)}
                >
                  <Pencil className="size-4" aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-sand-600 hover:text-terracotta dark:text-sand-300"
                  aria-label={t('admin.categories.actions.delete', { name: category.name })}
                  onClick={() => onDelete(category)}
                >
                  <Trash2 className="size-4" aria-hidden />
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
